/**
 * General File Service Types
 *
 * Types for file operations not scoped to a specific matter
 */

import { z } from 'zod';

// Core Types

/**
 * File status
 */
export type FileStatus = 'uploaded' | 'processing' | 'available' | 'failed';

/**
 * Supported file types
 */
export type FileType =
  | 'death-certificate'
  | 'will'
  | 'codicil'
  | 'grant-of-representation'
  | 'primary-id'
  | 'secondary-id'
  | 'correspondence-attachment'
  | 'institution-form'
  | 'institution-form-template'
  | 'institution-form-signed'
  | 'letterhead'
  | 'handwritten-signature'
  | 'email-signature'
  | 'distribution-declaration'
  | 'matter-document'
  | 'suggestion-attachment';

/**
 * File entity
 */
export interface FileEntity {
  id: number;
  originalFilename: string;
  status: FileStatus;
  s3Key: string;
  imageS3Key?: string;
  mimeType: string;
  sizeBytes: number;
  fileType: string;
  extractedData?: Record<string, unknown>;
  isCertifiedCopy: boolean;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string | null;

  // Relations
  matterId?: number;
  uploadedBy?: {
    id: number;
    email: string;
    firstName: string;
    lastName: string;
  };
}

// Request/Response Types

/**
 * Get presigned URL request
 */
export interface GetPresignedUrlRequest {
  fileType: FileType;
  matterId?: number;
}

/**
 * Presigned URL response
 */
export interface PresignedUrlResponse {
  presignedUrl: string;
  fileId: number;
}

/**
 * Upload complete request
 */
export interface UploadCompleteRequest {
  fileId: number;
  originalFilename: string;
}

/**
 * What `POST /files/:fileId/upload-complete` actually answers with
 * (`UploadCompleteResponseDto` backend-side) — an acknowledgement, NOT the file
 * row. Typing this as `FileEntity` is what made callers read `.id` off an
 * object that has none.
 */
export interface UploadCompleteResponse {
  fileId: number;
  fieldClassificationReport?: unknown;
}

/**
 * Update file request
 */
export interface UpdateFileRequest {
  isCertifiedCopy?: boolean;
}

/**
 * Download response
 */
export interface FileDownloadResponse {
  downloadUrl: string;
  filename: string;
  mimeType: string;
  sizeBytes: number;
}

// Validation Schemas

export const FileTypeSchema = z.enum([
  'death-certificate',
  'will',
  'codicil',
  'grant-of-representation',
  'primary-id',
  'secondary-id',
  'correspondence-attachment',
  'institution-form',
  'institution-form-template',
  'institution-form-signed',
  'letterhead',
  'handwritten-signature',
  'email-signature',
  'distribution-declaration',
  'matter-document',
  'suggestion-attachment'
]);

export const FileStatusSchema = z.enum([
  'uploaded',
  'processing',
  'available',
  'failed'
]);

export const GetPresignedUrlRequestSchema = z.object({
  fileType: FileTypeSchema,
  matterId: z.number().int().positive().optional()
});

export const UploadCompleteRequestSchema = z.object({
  fileId: z.number().int().positive(),
  originalFilename: z.string().min(1).max(255)
});

export const UpdateFileRequestSchema = z.object({
  isCertifiedCopy: z.boolean().optional()
});

// Action Result Types

export interface FileActionResult<T> {
  success: boolean;
  data?: T;
  error?: string;
  validationErrors?: Record<string, string[]>;
}

// File Type Metadata

export interface FileTypeMetadata {
  label: string;
  description: string;
  acceptedMimeTypes: string[];
  maxSizeBytes: number;
  requiresCertification: boolean;
  icon?: string;
}

export const FILE_TYPE_METADATA: Record<FileType, FileTypeMetadata> = {
  'death-certificate': {
    label: 'Death Certificate',
    description: 'Official death certificate from vital records',
    acceptedMimeTypes: [
      'application/pdf',
      'image/jpeg',
      'image/png',
      'image/jpg'
    ],
    maxSizeBytes: 20 * 1024 * 1024, // 20MB
    requiresCertification: true,
    icon: 'FileText'
  },
  will: {
    label: 'Will',
    description: 'Last will and testament',
    acceptedMimeTypes: [
      'application/pdf',
      'image/jpeg',
      'image/png',
      'image/jpg'
    ],
    maxSizeBytes: 50 * 1024 * 1024, // 50MB
    requiresCertification: true,
    icon: 'ScrollText'
  },
  codicil: {
    label: 'Codicil',
    description: 'A document that amends the will',
    acceptedMimeTypes: [
      'application/pdf',
      'image/jpeg',
      'image/png',
      'image/jpg'
    ],
    maxSizeBytes: 50 * 1024 * 1024, // 50MB
    requiresCertification: true,
    icon: 'ScrollText'
  },
  'grant-of-representation': {
    label: 'Grant of Representation',
    description: 'Court-issued grant of representation',
    acceptedMimeTypes: [
      'application/pdf',
      'image/jpeg',
      'image/png',
      'image/jpg'
    ],
    maxSizeBytes: 20 * 1024 * 1024, // 20MB
    requiresCertification: true,
    icon: 'Scale'
  },
  'primary-id': {
    label: 'Primary ID',
    description: 'Government-issued primary identification',
    acceptedMimeTypes: [
      'application/pdf',
      'image/jpeg',
      'image/png',
      'image/jpg'
    ],
    maxSizeBytes: 20 * 1024 * 1024, // 20MB
    requiresCertification: true,
    icon: 'IdCard'
  },
  'secondary-id': {
    label: 'Secondary ID',
    description: 'Secondary identification document',
    acceptedMimeTypes: [
      'application/pdf',
      'image/jpeg',
      'image/png',
      'image/jpg'
    ],
    maxSizeBytes: 20 * 1024 * 1024, // 20MB
    requiresCertification: false,
    icon: 'IdCard'
  },
  'correspondence-attachment': {
    label: 'Correspondence Attachment',
    description: 'File attached to correspondence',
    acceptedMimeTypes: [
      'application/pdf',
      'image/jpeg',
      'image/jpg',
      'image/png',
      'image/gif',
      'image/webp',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/plain',
      'text/csv',
      'application/rtf',
      'application/zip'
    ],
    maxSizeBytes: 25 * 1024 * 1024, // 25MB
    requiresCertification: false,
    icon: 'Paperclip'
  },
  'institution-form': {
    label: 'Institution Form',
    description: 'Form from financial institution',
    acceptedMimeTypes: ['application/pdf'],
    maxSizeBytes: 20 * 1024 * 1024, // 20MB
    requiresCertification: false,
    icon: 'FileText'
  },
  'institution-form-template': {
    label: 'Institution Form Template',
    description: 'Template for institution forms',
    acceptedMimeTypes: ['application/pdf'],
    maxSizeBytes: 20 * 1024 * 1024, // 20MB
    requiresCertification: false,
    icon: 'FileText'
  },
  'institution-form-signed': {
    label: 'Signed Institution Form',
    description: 'Signed institution form',
    acceptedMimeTypes: ['application/pdf'],
    maxSizeBytes: 20 * 1024 * 1024, // 20MB
    requiresCertification: false,
    icon: 'FileCheck'
  },
  letterhead: {
    label: 'Letterhead',
    description: 'Law firm letterhead',
    acceptedMimeTypes: [
      'application/pdf',
      'image/jpeg',
      'image/png',
      'image/jpg'
    ],
    maxSizeBytes: 20 * 1024 * 1024, // 20MB
    requiresCertification: false,
    icon: 'FileText'
  },
  'handwritten-signature': {
    label: 'Handwritten Signature',
    description: 'Handwritten signature image',
    acceptedMimeTypes: ['image/jpeg', 'image/png', 'image/jpg'],
    maxSizeBytes: 5 * 1024 * 1024, // 5MB
    requiresCertification: false,
    icon: 'PenTool'
  },
  'email-signature': {
    label: 'Email Signature',
    description: 'Email signature template',
    acceptedMimeTypes: ['text/html', 'text/plain'],
    maxSizeBytes: 5 * 1024 * 1024, // 5MB
    requiresCertification: false,
    icon: 'Mail'
  },
  'distribution-declaration': {
    label: 'Distribution Declaration',
    description: "A beneficiary's distribution declaration document",
    acceptedMimeTypes: [
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ],
    maxSizeBytes: 25 * 1024 * 1024, // 25MB
    requiresCertification: false,
    icon: 'file-signature'
  },
  'matter-document': {
    label: 'Matter Document',
    description: 'General document related to the matter',
    acceptedMimeTypes: [
      'application/pdf',
      'image/jpeg',
      'image/jpg',
      'image/png',
      'image/gif',
      'image/webp',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/plain',
      'text/csv',
      'application/rtf',
      'application/zip'
    ],
    maxSizeBytes: 25 * 1024 * 1024, // 25MB
    requiresCertification: false,
    icon: 'File'
  },
  'suggestion-attachment': {
    label: 'Suggestion Attachment',
    description: 'File attached to a product suggestion',
    acceptedMimeTypes: [
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'image/jpeg',
      'image/jpg',
      'image/png',
      'video/mp4',
      'video/quicktime',
      'video/x-msvideo',
      'video/webm',
      'application/zip',
      'application/x-zip-compressed',
      'application/x-rar-compressed',
      'application/x-7z-compressed'
    ],
    maxSizeBytes: 5 * 1024 * 1024, // 5MB
    requiresCertification: false,
    icon: 'MessageSquare'
  }
};
